import L from 'leaflet'
import { useEffect, useRef } from 'react'

import { PlanningShift } from '@/entities/schedules/models/planning/PlanningShift'
import { RunsheetShift } from '@/entities/schedules/models/runsheet/RunsheetShift'
import { CurrentScreenType } from '@/entities/userInterface/model/CurrentScreenType'
import { OLMSApiService } from '@/shared/apis'
import { EntityId } from '@reduxjs/toolkit'

import { PlanningPathResponse, RoutePath } from '../models/PlanningPath'

export type DrawRoutesArgs = {
  map: L.Map | null
  allShifts: PlanningShift[] | RunsheetShift[]
  selectedShiftIds: EntityId[],
  currentScreen: CurrentScreenType
}

/**
 * The following hook is used to draw the routes on the map widget.
 *  It stores the route path returned from the API in a reference to avoid re-rendering, and
 *    update the map with the new route path when the selected shifts change.
 * 
 * @param map the Leaflet map object
 * @param allShifts all the shifts stored in the redux store
 * @param selectedShiftIds the selected shift ids from the context
 * @param currentScreen either 'planning' or 'runsheet'
 * @returns void
 */
export const useDrawRoutes = ({
  map,
  allShifts,
  selectedShiftIds,
  currentScreen,
}: DrawRoutesArgs) => {
  const routeLayersRef = useRef<Map<string | number, L.LayerGroup | null>>(new Map())
  const pathCache = useRef<Map<string | number, RoutePath>>(new Map())

  const fetchRoutePath = (shiftOptions: { routeDisplay: number, shiftIds: EntityId[] }) => {
    const endpoint = currentScreen === 'planning' ? '/planning/Path' : '/runsheet/Path'

    return OLMSApiService.post<PlanningPathResponse>(endpoint, shiftOptions)
  }

  const updateRoutesOnMap = () => {
    // Clear all existing route layers and add new ones
    routeLayersRef.current.forEach((layer) => {
      layer?.clearLayers()
    })

    selectedShiftIds.forEach((shiftId) => {
      const path = pathCache.current.get(shiftId)

      if (path) {
        addRouteToMap(path)
      }
    })
  }

  const addRouteToMap = (routePath: RoutePath) => {
    if (!map || !routePath?.points) return

    const shiftColor = allShifts.filter(shift => shift.id === routePath.shiftId)?.[0].uiColor || 'blue'
    const latlngs = routePath.points.map(point => new L.LatLng(point.y, point.x))
    const polyline = L.polyline(latlngs, {
      color: shiftColor,
    })

    const layer = L.layerGroup([polyline]).addTo(map)

    routeLayersRef.current.set(routePath.shiftId, layer)
  }

  const removeRouteFromMap = (shiftId: EntityId) => {
    if (!routeLayersRef.current) return

    routeLayersRef.current.forEach((layer, key) => {
      if (key === shiftId) {
        layer?.remove()
      }
    })
  }

  // Update routes on the map when selected shifts change
  useEffect(() => {
    const newShiftIds = selectedShiftIds.filter((id) => !pathCache.current.has(id))

    if (newShiftIds.length > 0) {
      fetchRoutePath({ routeDisplay: 1, shiftIds: newShiftIds })
        .then((routes) => {
          if (!routes) return

          routes.forEach((route) => {
            pathCache.current.set(route.shiftId as string, route)
          })
          updateRoutesOnMap()
        })
    } else {
      updateRoutesOnMap()
    }

    // Check shifts for deselected routes and remove them from the map
    const drawnShiftId = Array.from(routeLayersRef.current.keys())
    const deselectedShiftIds = drawnShiftId.filter((id) => !selectedShiftIds.includes(id))

    deselectedShiftIds.forEach((id) => {
      removeRouteFromMap(id)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShiftIds, map])
}